import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import HomeIcon from '@/shared/assets/sidebar/home-icon.svg?react';
import WalletIcon from '@/shared/assets/sidebar/wallets-icon.svg?react';
import ExchangeIcon from '@/shared/assets/sidebar/exchange-icon.svg?react';
import OtcExchangeIcon from '@/shared/assets/sidebar/otc-icon.svg?react';
import TradingAccountsIcon from '@/shared/assets/sidebar/trade-accounts-icon.svg?react';
import CandlesIcon from '@/shared/assets/sidebar/terminal-icon.svg?react';
import OpenNewTabRoundedIcon from '@/shared/assets/sidebar/external-icon.svg?react';
import MarketsIcon from '@/shared/assets/sidebar/markets-icon.svg?react';
import ClockIcon from '@/shared/assets/sidebar/history-icon.svg?react';
import { useUserScopeConfiguration } from '@/entities/user';
import { useAuthorizedTradingUrl } from '@/shared/api/mutations/useAuthorizedTradingUrl';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import { useInvoiceHistory } from '@/features/deposit-bank-transfer';
import type { NavGroup } from './types';

const useNavigationGroups = () => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const { pathname } = useLocation();
  const { userScopeConfiguration } = useUserScopeConfiguration();
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { getUrl } = useAuthorizedTradingUrl();
  const { data: pendingInvoices } = useInvoiceHistory();
  const pendingInvoicesCount = pendingInvoices?.length ?? 0;

  const isVerified = session?.additional?.isVerified || false;

  const parseRoute = useCallback(
    (href: string, onClick?: () => void) => {
      if (!isVerified) {
        return {
          href: '',
          onClick: openVerificationInfoModal,
        };
      }

      return {
        href,
        onClick,
      };
    },
    [isVerified, openVerificationInfoModal]
  );

  const groups = useMemo<NavGroup[]>(() => {
    const allGroups: NavGroup[] = [
      {
        key: 'main',
        items: [
          {
            key: 'home',
            icon: HomeIcon,
            label: t('personalAccount.sidebarNavigation.home'),
            href: '/',
          },
        ],
      },
      {
        key: 'assets',
        title: t('personalAccount.sidebarNavigation.groups.assets'),
        items: [
          {
            key: 'wallets',
            icon: WalletIcon,
            label: t('personalAccount.sidebarNavigation.wallets'),
            href: '/wallets',
          },
          {
            key: 'exchange',
            icon: ExchangeIcon,
            label: t('personalAccount.sidebarNavigation.exchange'),
            href: '/exchange',
          },
          {
            key: 'otc',
            icon: OtcExchangeIcon,
            label: t('personalAccount.sidebarNavigation.otcExchange'),
            ...parseRoute('/otc'),
            display: Boolean(userScopeConfiguration?.exchange.otc),
          },
        ],
      },
      {
        key: 'trading',
        title: t('personalAccount.sidebarNavigation.groups.trading'),
        items: [
          {
            key: 'tradingAccounts',
            icon: TradingAccountsIcon,
            label: t('personalAccount.sidebarNavigation.tradingAccounts'),
            href: '/trading-accounts',
          },
          {
            key: 'terminal',
            icon: CandlesIcon,
            label: t('personalAccount.sidebarNavigation.terminal'),
            ...parseRoute('', getUrl),
            external: true,
            rightIcon: OpenNewTabRoundedIcon,
          },
        ],
      },
      {
        key: 'analytics',
        title: t('personalAccount.sidebarNavigation.groups.analytics'),
        items: [
          {
            key: 'markets',
            icon: MarketsIcon,
            label: t('personalAccount.sidebarNavigation.markets'),
            href: '/markets',
          },
          {
            key: 'history',
            icon: ClockIcon,
            label: t('personalAccount.sidebarNavigation.history'),
            href: '/history',
            activePatterns: ['/history'],
            counter: pendingInvoicesCount,
          },
        ],
      },
    ];

    return allGroups
      .map(group => ({
        ...group,
        items: group.items.filter(
          item => item.display === undefined || item.display
        ),
      }))
      .filter(group => group.items.length > 0);
  }, [t, userScopeConfiguration, parseRoute, getUrl, pendingInvoicesCount]);

  return { groups, pathname };
};

export { useNavigationGroups };
